import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trophy, ArrowsClockwise, Trash } from "@phosphor-icons/react";

/**
 * RetaBracket — UI for generating + viewing + editing a piramidal knockout bracket
 * derived from court planilla rankings.
 */
export default function RetaBracket({ retaId, canEdit, onClose }) {
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [customSize, setCustomSize] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [roster, setRoster] = useState([]); // all players in reta

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        api.get(`/retas/${retaId}/bracket`),
        api.get(`/retas/${retaId}/bracket/roster`).catch(() => ({ data: { players: [] } })),
      ]);
      setBracket(b.data?.bracket || null);
      setRoster(r.data?.players || []);
    } catch (e) {
      toast.error("Error al cargar bracket");
    } finally { setLoading(false); }
  }, [retaId]);

  useEffect(() => { load(); }, [load]);

  const generate = async (sizeOverride = null) => {
    if (generating) return;
    setGenerating(true);
    try {
      const body = {};
      if (sizeOverride && sizeOverride > 0) body.custom_size = sizeOverride;
      const r = await api.post(`/retas/${retaId}/bracket/generate`, body);
      setBracket(r.data.bracket);
      toast.success("Bracket generado");
      setShowCustom(false);
      setCustomSize("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el bracket");
    } finally { setGenerating(false); }
  };

  const reset = async () => {
    if (!window.confirm("¿Eliminar bracket actual? Podrás regenerarlo después.")) return;
    try {
      await api.delete(`/retas/${retaId}/bracket`);
      setBracket(null);
      toast.success("Bracket reseteado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Cargando bracket...</div>;
  }

  if (!bracket) {
    return (
      <div className="text-center py-12 space-y-4" data-testid="bracket-empty">
        <Trophy size={48} weight="duotone" className="mx-auto text-amber-400/50" />
        <div className="text-sm text-gray-400">
          No hay bracket generado todavía. Se generará a partir de los <span className="text-[#CCFF00] font-bold">top 2</span> jugadores con más puntos de cada planilla.
        </div>
        {canEdit && (
          <div className="space-y-2">
            <Button onClick={() => generate()} disabled={generating} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold uppercase" data-testid="bracket-generate-btn">
              {generating ? "Generando..." : <><Trophy size={16} weight="bold" className="mr-2" /> Generar desde Planillas</>}
            </Button>
            <div className="text-[10px] text-gray-500">— o —</div>
            {!showCustom ? (
              <Button onClick={() => setShowCustom(true)} variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid="bracket-custom-toggle">
                Tamaño personalizado
              </Button>
            ) : (
              <div className="flex gap-2 items-center justify-center max-w-xs mx-auto">
                <Input type="number" min="1" max="1024" value={customSize} onChange={(e) => setCustomSize(e.target.value)} placeholder="Partidos (1-1024)" className="bg-black border-amber-500/30 text-white" data-testid="bracket-custom-size-input" />
                <Button onClick={() => { const n = parseInt(customSize); if (n > 0 && n <= 1024) generate(n); else toast.error("Tamaño 1-1024"); }} disabled={generating} className="bg-amber-500 text-black hover:bg-amber-600 font-bold" data-testid="bracket-custom-generate-btn">Crear</Button>
              </div>
            )}
            {showCustom && customSize && parseInt(customSize) > 64 && (
              <div className="text-[10px] text-amber-400">⚠ Brackets de más de 64 partidos pueden ser difíciles de manejar en pantalla pequeña.</div>
            )}
          </div>
        )}
      </div>
    );
  }

  const champion = bracket.champion;

  return (
    <div className="space-y-5" data-testid="bracket-content">
      {/* Header info */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] tracking-widest uppercase text-gray-500">
          Generado: {bracket.generated_at?.slice(0, 16).replace("T", " ")}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={generate} disabled={generating} className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 text-[10px] uppercase font-bold h-7" data-testid="bracket-regen-btn">
              <ArrowsClockwise size={12} className="mr-1" /> Regenerar
            </Button>
            <Button size="sm" variant="outline" onClick={reset} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] uppercase font-bold h-7" data-testid="bracket-reset-btn">
              <Trash size={12} className="mr-1" /> Resetear
            </Button>
          </div>
        )}
      </div>

      {/* Champion banner */}
      {champion && champion.name && (
        <div className="bg-gradient-to-r from-amber-500/30 via-yellow-500/40 to-amber-500/30 border-2 border-amber-400 rounded-lg p-4 text-center" data-testid="bracket-champion">
          <Trophy size={32} weight="fill" className="text-amber-300 mx-auto mb-1" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-amber-200">🏆 Campeón 🏆</div>
          <div className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-white">{champion.name}</div>
        </div>
      )}

      {/* Finalists by court */}
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-bold mb-2">Finalistas (Top 2 por cancha)</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(
            (bracket.finalists || []).reduce((acc, f) => {
              const k = f.source_court_name;
              (acc[k] = acc[k] || []).push(f);
              return acc;
            }, {})
          ).map(([court, players]) => (
            <div key={court} className="bg-[#141414] border border-white/10 rounded p-2">
              <div className="text-[9px] tracking-widest uppercase text-[#CCFF00] mb-1 font-bold">{court}</div>
              {players.sort((a, b) => a.source_rank - b.source_rank).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs mb-1">
                  <span className="text-white truncate flex items-center gap-1">
                    <span className="text-[9px] text-gray-500 font-mono">#{p.source_rank}</span>
                    {p.name}
                  </span>
                  <Badge className="bg-[#CCFF00]/20 text-[#CCFF00] border-[#CCFF00]/30 text-[9px]">{p.total_points} pts</Badge>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Rounds */}
      <div className="flex gap-4 overflow-x-auto pb-3" data-testid="bracket-rounds">
        {(bracket.rounds || []).map((round, rIdx) => (
          <div key={rIdx} className="min-w-[240px] flex-shrink-0 space-y-3">
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-amber-300 text-center pb-2 border-b border-amber-500/30">
              {round.name}
            </div>
            <div className={`space-y-${rIdx === 0 ? "3" : (rIdx === 1 ? "8" : "16")} pt-${rIdx > 0 ? "8" : "0"}`}>
              {(round.matches || []).map((match, mIdx) => (
                <BracketMatch
                  key={match.id}
                  retaId={retaId}
                  match={match}
                  canEdit={canEdit}
                  roster={roster}
                  onUpdated={(newBracket) => setBracket(newBracket)}
                  testId={`bracket-match-${rIdx}-${mIdx}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <Button onClick={onClose} variant="outline" className="border-white/10" data-testid="bracket-close-btn">Cerrar</Button>
      </div>
    </div>
  );
}

// ─── Helpers ───
const teamDisplay = (slot) => {
  if (!slot) return "TBD";
  const name = slot.name || "TBD";
  const partner = slot.partner_name;
  return partner ? `${name} / ${partner}` : name;
};

// ─── Compact score input (0-15) ───
function ScoreInput({ value, onCommit, disabled, testId }) {
  const [local, setLocal] = useState(value ?? "");
  useEffect(() => { setLocal(value ?? ""); }, [value]);
  const commit = () => {
    if (local === "" || local === null) {
      onCommit?.(null);
      return;
    }
    const n = Math.max(0, Math.min(15, parseInt(local) || 0));
    setLocal(n);
    if (n !== (value ?? -1)) onCommit?.(n);
  };
  return (
    <input
      type="number"
      min="0"
      max="15"
      value={local === null ? "" : local}
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
      className="w-9 h-7 text-center bg-black border border-white/15 rounded text-xs font-mono font-bold text-amber-300 focus:border-amber-500 focus:outline-none disabled:opacity-40"
      data-testid={testId}
    />
  );
}

// ─── Bracket Match Card ───
function BracketMatch({ retaId, match, canEdit, roster = [], onUpdated, testId }) {
  const [editing, setEditing] = useState(false);
  const [editPlayers, setEditPlayers] = useState(false);
  const [score, setScore] = useState(match.score || "");
  const [winner, setWinner] = useState(match.winner || "");
  const [saving, setSaving] = useState(false);

  const p1Name = teamDisplay(match.p1);
  const p2Name = teamDisplay(match.p2);
  const ready = p1Name !== "TBD" && p2Name !== "TBD" && p2Name !== "BYE";

  const save = async () => {
    if (!winner || (winner !== "p1" && winner !== "p2")) {
      toast.error("Selecciona ganador (P1 o P2)");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/retas/${retaId}/bracket/match/${match.id}`, { score, winner });
      onUpdated?.(r.data.bracket);
      setEditing(false);
      toast.success("Resultado guardado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const replacePlayer = async (slot, payload) => {
    try {
      const body = {
        slot,
        name: payload.name,
        user_id: payload.user_id || null,
      };
      if (payload.partner_name) {
        body.partner_name = payload.partner_name;
        body.partner_user_id = payload.partner_user_id || null;
      }
      const r = await api.put(`/retas/${retaId}/bracket/match/${match.id}/player`, body);
      onUpdated?.(r.data.bracket);
      toast.success(`${slot.toUpperCase()} actualizado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar jugador");
    }
  };

  const updateScore = async (slot, val) => {
    try {
      const body = slot === "p1" ? { p1_score: val } : { p2_score: val };
      const r = await api.put(`/retas/${retaId}/bracket/match/${match.id}/scores`, body);
      onUpdated?.(r.data.bracket);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar puntaje");
    }
  };

  const p1Win = match.winner === "p1";
  const p2Win = match.winner === "p2";

  return (
    <div className={`bg-[#141414] border-2 rounded p-2 transition-colors ${match.winner ? "border-amber-500/50" : ready ? "border-white/20" : "border-white/5 opacity-80"}`} data-testid={testId}>
      {/* P1 row */}
      <div className={`flex items-center gap-2 px-2 py-1.5 rounded ${p1Win ? "bg-amber-500/20" : ""}`}>
        <span className={`text-xs flex-1 truncate ${p1Win ? "text-amber-200 font-bold" : p1Name === "TBD" ? "text-gray-600 italic" : "text-white"}`}>
          {p1Win && "🏆 "}{p1Name}
        </span>
        {canEdit && ready && (
          <ScoreInput value={match.p1_score} onCommit={(v) => updateScore("p1", v)} testId={`${testId}-p1-score`} />
        )}
        {!canEdit && match.p1_score !== undefined && match.p1_score !== null && (
          <span className="text-xs font-mono font-bold text-amber-300 w-9 text-center">{match.p1_score}</span>
        )}
      </div>
      <div className="h-px bg-white/5 my-1" />
      {/* P2 row */}
      <div className={`flex items-center gap-2 px-2 py-1.5 rounded ${p2Win ? "bg-amber-500/20" : ""}`}>
        <span className={`text-xs flex-1 truncate ${p2Win ? "text-amber-200 font-bold" : (p2Name === "BYE" || p2Name === "TBD") ? "text-gray-600 italic" : "text-white"}`}>
          {p2Win && "🏆 "}{p2Name}
        </span>
        {canEdit && ready && (
          <ScoreInput value={match.p2_score} onCommit={(v) => updateScore("p2", v)} testId={`${testId}-p2-score`} />
        )}
        {!canEdit && match.p2_score !== undefined && match.p2_score !== null && (
          <span className="text-xs font-mono font-bold text-amber-300 w-9 text-center">{match.p2_score}</span>
        )}
      </div>

      {canEdit && (
        <div className="mt-2 space-y-1">
          {/* Player editor toggle */}
          {!editing && (
            <Button size="sm" onClick={() => setEditPlayers(!editPlayers)} variant="outline" className={`w-full text-[10px] uppercase h-6 ${editPlayers ? "bg-amber-500/20 border-amber-500/50 text-amber-200" : "border-white/10 text-gray-400 hover:text-white hover:border-white/30"}`} data-testid={`${testId}-edit-players`}>
              {editPlayers ? "✓ Ocultar" : "✎ Cambiar jugadores"}
            </Button>
          )}
          {editPlayers && !editing && (
            <div className="space-y-2 pt-1 border-t border-amber-500/20">
              <TeamPicker label="Equipo A (P1)" current={match.p1 || {}} roster={roster} onSave={(team) => replacePlayer("p1", team)} testId={`${testId}-p1-pick`} />
              <TeamPicker label="Equipo B (P2)" current={match.p2 || {}} roster={roster} onSave={(team) => replacePlayer("p2", team)} testId={`${testId}-p2-pick`} />
            </div>
          )}
          {/* Result editor */}
          {ready && !editPlayers && !editing && (
            <Button size="sm" onClick={() => setEditing(true)} className="w-full bg-white/5 hover:bg-white/10 text-[10px] uppercase h-7" data-testid={`${testId}-edit`}>
              {match.winner ? "Editar resultado" : "Resultado"}
            </Button>
          )}
          {editing && (
            <div className="mt-1 space-y-1.5 pt-2 border-t border-white/10">
              <Input value={score} onChange={(e) => setScore(e.target.value)} placeholder="6-4, 6-2" className="h-7 text-xs bg-black border-white/10" data-testid={`${testId}-score`} />
              <div className="grid grid-cols-2 gap-1">
                <button type="button" onClick={() => setWinner("p1")} className={`text-[10px] uppercase font-bold py-1 rounded ${winner === "p1" ? "bg-amber-500 text-black" : "bg-white/5 text-white hover:bg-white/10"}`} data-testid={`${testId}-win-p1`}>
                  {(p1Name.split(" ")[0] || "P1")}
                </button>
                <button type="button" onClick={() => setWinner("p2")} className={`text-[10px] uppercase font-bold py-1 rounded ${winner === "p2" ? "bg-amber-500 text-black" : "bg-white/5 text-white hover:bg-white/10"}`} data-testid={`${testId}-win-p2`}>
                  {(p2Name.split(" ")[0] || "P2")}
                </button>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setScore(match.score || ""); setWinner(match.winner || ""); }} className="border-white/10 text-[10px] h-7 flex-1">Cancelar</Button>
                <Button size="sm" onClick={save} disabled={saving} className="bg-amber-500 text-black hover:bg-amber-600 text-[10px] font-bold h-7 flex-1" data-testid={`${testId}-save`}>{saving ? "..." : "Guardar"}</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Team Picker (pareja: jugador A + jugador B) ───
function TeamPicker({ label, current, roster, onSave, testId }) {
  const [a, setA] = useState({ name: current.name || "", user_id: current.user_id || null });
  const [b, setB] = useState({ name: current.partner_name || "", user_id: current.partner_user_id || null });

  // Sync when match data changes externally
  useEffect(() => {
    setA({ name: current.name || "", user_id: current.user_id || null });
    setB({ name: current.partner_name || "", user_id: current.partner_user_id || null });
  }, [current.name, current.user_id, current.partner_name, current.partner_user_id]);

  const dirty = (a.name !== (current.name || "")) ||
                (b.name !== (current.partner_name || ""));

  const save = () => {
    if (!a.name.trim()) { toast.error("Jugador A requerido"); return; }
    onSave({
      name: a.name.trim(),
      user_id: a.user_id,
      partner_name: b.name.trim() || undefined,
      partner_user_id: b.user_id,
    });
  };

  return (
    <div className="space-y-1.5 bg-black/40 border border-amber-500/20 rounded p-2" data-testid={testId}>
      <div className="text-[10px] tracking-widest uppercase font-bold text-amber-300">{label}</div>
      <PlayerPicker label="A" current={a.name || "TBD"} roster={roster} onPick={(p) => setA({ name: p.name, user_id: p.user_id })} testId={`${testId}-a`} />
      <PlayerPicker label="B" current={b.name || "(sin pareja)"} roster={roster} onPick={(p) => setB({ name: p.name === "TBD" ? "" : p.name, user_id: p.user_id })} testId={`${testId}-b`} />
      {dirty && (
        <Button size="sm" onClick={save} className="w-full h-6 bg-amber-500 text-black hover:bg-amber-600 text-[10px] font-bold uppercase" data-testid={`${testId}-save`}>
          Guardar equipo
        </Button>
      )}
    </div>
  );
}

// ─── Player Picker dropdown ───
function PlayerPicker({ label, current, roster, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const filtered = !filter ? roster : roster.filter((p) => (p.name || "").toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-black/60 border border-white/10 hover:border-amber-500/50"
        data-testid={testId}
      >
        <span className="text-[10px] tracking-widest uppercase text-amber-300 font-bold shrink-0">{label}:</span>
        <span className="text-xs text-white truncate flex-1">{current}</span>
        <span className="text-[10px] text-gray-500">▼</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 right-0 bg-[#0A0A0A] border border-amber-500/40 rounded shadow-xl max-h-64 overflow-y-auto" data-testid={`${testId}-menu`}>
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Buscar..."
            className="m-1 h-7 text-xs bg-black border-white/10"
            data-testid={`${testId}-filter`}
          />
          <button
            type="button"
            onClick={() => { onPick({ name: "TBD", user_id: null }); setOpen(false); }}
            className="w-full text-left px-2 py-1 text-xs text-gray-500 italic hover:bg-white/5"
          >→ Vaciar (TBD)</button>
          <button
            type="button"
            onClick={() => { onPick({ name: "BYE", user_id: null }); setOpen(false); }}
            className="w-full text-left px-2 py-1 text-xs text-gray-500 italic hover:bg-white/5"
          >→ BYE</button>
          {filtered.length === 0 ? (
            <div className="px-2 py-2 text-[10px] text-gray-600">Sin jugadores</div>
          ) : (
            filtered.map((p, i) => (
              <button
                key={(p.user_id || p.name) + i}
                type="button"
                onClick={() => { onPick(p); setOpen(false); }}
                className="w-full text-left px-2 py-1.5 text-xs text-white hover:bg-amber-500/10 flex items-center justify-between gap-2"
                data-testid={`${testId}-opt-${i}`}
              >
                <span className="truncate">{p.name}{p.is_guest ? " (inv)" : ""}</span>
                <span className="text-[9px] text-gray-500 shrink-0">{p.source_court}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
